import React from "react";
import { I18n } from "react-redux-i18n";
import cx from 'classnames';

import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import { Checkbox } from "@common/components/checkbox";

import "./options.styles.scss";

export type OptionsItem = {
  id: string;
  className: string;
  read: boolean;
  write: boolean;
};

export type OptionsProps = {
  getId?: {
    checkbox?: (params: { item: OptionsItem, type: 'read' | 'write'; rowIndex: number; columnIndex: number }) => string;
    cell?: (params: { item: OptionsItem; rowIndex: number; columnIndex: number }) => string;
  };
  disabled: boolean;
  data: Array<OptionsItem>;
  onChange: (options: Array<OptionsItem>) => void;
};

export const Options: React.FC<OptionsProps> = (props) => {
  const { data, disabled, onChange, getId } = props;

  const handleChange = React.useCallback((updatedItem: OptionsItem) => {
    onChange(data.map((x) => x.id === updatedItem.id ? updatedItem : x));
  }, [data, onChange]);

  const renderReadCheckbox = React.useCallback((item: OptionsItem, { rowIndex, colIndex }: { rowIndex: number; colIndex: number }) => {
    return (
      <Checkbox 
        checked={item.read}
        {...getTestAttributes({ id: 
          getId?.checkbox 
            ? getId?.checkbox({ item, type: 'read', rowIndex, columnIndex: colIndex }) 
            : `sec-checkbox-row-${rowIndex + 1}-col-${colIndex}-read`
        })}
        onChange={disabled ? undefined : () => handleChange({ ...item, read: !item.read })}
      />
    );
  }, [handleChange, disabled]);

  const renderWriteCheckbox = React.useCallback((item: OptionsItem, { rowIndex, colIndex }: { rowIndex: number; colIndex: number }) => {
    return (
      <Checkbox 
        checked={item.write}
        {...getTestAttributes({ id: 
          getId?.checkbox 
            ? getId?.checkbox({ item, type: 'write', rowIndex, columnIndex: colIndex }) 
            : `sec-checkbox-row-${rowIndex + 1}-col-${colIndex}-write`
        })}
        onChange={disabled ? undefined : () => handleChange({ ...item, write: !item.write })}
      />
    );
  }, [handleChange, disabled]);

  const columns = React.useMemo(() => [
    {
      id: 'name',
      name: I18n.t('Class Name'),
      fieldName: 'className',
      width: '60%',
    },
    {
      id: 'read',
      name: I18n.t('Read?'),
      width: '20%',
      render: renderReadCheckbox
    },
    {
      id: 'write',
      name: I18n.t('Write?'),
      width: '20%',
      render: renderWriteCheckbox
    },
  ], [renderReadCheckbox, renderWriteCheckbox]);

  return (
    <div className="ItemSecurityManagementOptions">
      <Flex justify="space-between" alignItems="center" className="ItemSecurityManagementOptions__list-header">
        {columns.map((x) => (
          <span key={x.id} style={{ width: x.width }}>{x.name}</span>
        ))}
      </Flex>
      <div className="ItemSecurityManagementOptions__list">
        {data.map((item, rowIndex) => (
          <Flex 
            justify="space-between" 
            alignItems="center" 
            key={item.id} 
            className={cx("ItemSecurityManagementOptions__list-row", { 'ItemSecurityManagementOptions__disabled-el': disabled })}
          >
            {columns.map((col, columnIndex) => (
              <React.Fragment key={col.id}>
                <span 
                  style={{ width: col.width }} 
                  {...getTestAttributes({ id: getId?.cell
                    ? getId?.cell({ item, rowIndex, columnIndex })
                    : `sec-grid-${rowIndex + 1}-${columnIndex}` 
                  })}
                >
                  {col.render 
                    ? col.render(item, { rowIndex, colIndex: columnIndex }) 
                    : (item as Record<string, string | number | boolean>)[col.fieldName]}
                </span>
              </React.Fragment>
            ))}
          </Flex>
        ))}
      </div>
    </div>
  );
};
